import React, { useState } from "react";
import PropTypes from "prop-types";
import { useMutation } from "@apollo/client";
import { EDIT_TEACHING } from "../../../apollo-client/mutations";
import Dialog from "@mui/material/Dialog";
import { FormattedMessage } from "react-intl";
import { useIsMobile } from "../../../hooks/state-checkers/use-is-mobile";
import { TeachingDialogLayout } from "./shared/TeachingDialogLayout";
import { TeachingFormFields } from "./shared/TeachingFormFields";
import { TeachingDialogActions } from "./shared/TeachingDialogActions";

export const EditTeachingDialog = ({ teaching, isDialogOpen, closeDialog }) => {
  const [formValues, setFormValues] = useState({
    price: teaching.price,
    acceptsRemote: teaching.acceptsRemote,
    description: teaching.description,
  });

  const [formValueErrors, setFormValueErrors] = useState({
    price: false,
    acceptsRemote: false,
    description: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const isMobile = useIsMobile();

  const [editTeaching] = useMutation(EDIT_TEACHING);

  const handleEditTeaching = async () => {
    setIsSubmitting(true);
    try {
      await editTeaching({
        variables: {
          id: teaching._id,
          price: parseInt(formValues.price),
          description: formValues.description,
          acceptsRemote: formValues.acceptsRemote,
        },
      });
      
      setIsSuccess(true);
      // Show success state briefly before closing
      setTimeout(() => {
        closeDialog();
        setIsSuccess(false);
        setIsSubmitting(false);
      }, 1500);
    } catch (err) {
      setIsSubmitting(false);
      // You might want to add error handling here
    }
  };

  const handleCloseDialog = () => {
    closeDialog();
    // Reset form values to original teaching values
    setFormValues({
      price: teaching.price,
      acceptsRemote: teaching.acceptsRemote,
      description: teaching.description,
    });
  };

  return (
    <Dialog
      open={isDialogOpen}
      onClose={() => handleCloseDialog()}
      fullWidth={true}
      fullScreen={isMobile}
      maxWidth={"md"}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          boxShadow: isMobile ? 'none' : '0 24px 38px 3px rgba(0,0,0,0.14), 0 9px 46px 8px rgba(0,0,0,0.12), 0 11px 15px -7px rgba(0,0,0,0.2)',
          overflow: 'hidden',
        },
      }}
    >
      <TeachingDialogLayout
        isOpen={isDialogOpen}
        isMobile={isMobile}
        onClose={handleCloseDialog}
        title={teaching.teacher.username}
        subtitle={<FormattedMessage id="teacher.edit-teaching" defaultMessage="Edit Teaching" />}
        showSuccess={isSuccess}
        successTitle={<FormattedMessage id="teacher.success.title" defaultMessage="Success!" />}
        successMessage={<FormattedMessage id="teacher.success.edit-message" defaultMessage="Teaching updated successfully!" />}
        actions={
          <TeachingDialogActions
            onCancel={handleCloseDialog}
            onConfirm={handleEditTeaching}
            confirmText={<FormattedMessage id="teacher.edit-teaching-complete" defaultMessage="Complete Edit" />}
            isSubmitting={isSubmitting}
            isDisabled={!!formValueErrors.price}
          />
        }
      >
        <TeachingFormFields
          formValues={formValues}
          formValueErrors={formValueErrors}
          onFormValuesChange={setFormValues}
          onFormValueErrorsChange={setFormValueErrors}
          isSubmitting={isSubmitting}
          showDescriptionValidation={false}
        />
      </TeachingDialogLayout>
    </Dialog>
  );
};

EditTeachingDialog.propTypes = {
  teaching: PropTypes.object.isRequired,
  isDialogOpen: PropTypes.bool.isRequired,
  closeDialog: PropTypes.func.isRequired,
};
